import { Injectable } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { lastValueFrom } from "rxjs";
import { AuthorisationService } from "../authorisation/authorisation.service";

@Injectable({
    providedIn: "root",
})
export class CommonIntegratedArchitectureFrameworkAuthService {
    public static EditRoles = "editRoles";
    public static EditAnyRole = "editAnyRole";
    public static EditRoleContents = "editRoleContents";

    public static ReadSystem = "readSystem";
    public static EditSystem = "editSystem";
    public static ConfigureSystem = "configureSystem";
    public static ConfigureAnySystem = "configureAnySystem";

    public constructor(
        private authorisationService: AuthorisationService,
        private commonTeamsAuthService: CommonTeamsAuthService,
        private commonIntegratedArchitectureFrameworkService: CommonIntegratedArchitectureFrameworkService,
    ) {}

    public static registerAccessVerifiers(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.ReadSystem,
            {
                requirePermissions: [
                    FeaturePermissionName.ArchitectureSystemRead,
                    FeaturePermissionName.ArchitectureSystemEdit,
                    FeaturePermissionName.ArchitectureSystemConfigure,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.EditAnyRole,
            {
                requirePermissions: FeaturePermissionName.ArchitectureRolesEdit,
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.ConfigureAnySystem,
            {
                requirePermissions: FeaturePermissionName.ArchitectureSystemConfigure,
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.EditRoleContents,
            {
                requirePermissions: [
                    FeaturePermissionName.ArchitectureRolesPersonalEdit,
                ],
            },
        );
    }

    public currentPersonCan(checker: (person: Person) => boolean) {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return checker(this.authorisationService.currentPerson);
    }

    @Autobind
    public currentPersonCanEntity<TEntity>(checker: (person: Person, entity?: TEntity) => Promise<boolean> | boolean, entity?: TEntity) {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return checker(this.authorisationService.currentPerson, entity);
    }

    @Autobind
    public personCanReadSystems(person: Person) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureSystemRead)
            || this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureSystemEdit)
            || this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureSystemConfigure);
    }

    public currentPersonCanReadSystems() {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return this.personCanReadSystems(this.authorisationService.currentPerson);
    }

    public async canEditRoleContents(role: Role) {
        // currentPersonCanEntity has return type of boolean | Promise -> so declare this function as async to let both wrapped to a promise
        return this.currentPersonCanEntity<Role>(this.personCanEditRoleContents, role);
    }

    public async canEditRoleByTeam(role: Role) {
        // currentPersonCanEntity has return type of boolean | Promise -> so declare this function as async to let both wrapped to a promise
        return this.currentPersonCanEntity<Role>(this.personCanEditRoleByTeam, role);
    }

    @Autobind
    public async personCanEditRoleContents(person: Person, role?: Role) {
        const canEditRoles = this.personCanEditRoles(person);
        const isAccessManager = this.authorisationService.personHasPermission(person, FeaturePermissionName.OrganisationAccessManagementConfigure);
        if (canEditRoles || isAccessManager) {
            return true;
        } else if (role) {
            if (await this.personCanEditRoleByTeam(person, role)) {
                return true;
            }

            const canEditOwnRole = this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureRolesPersonalEdit);
            if (canEditOwnRole) {
                return !!role.roleConnections.find((rc) => rc.connectionId === person.getLatestConnection()?.connectionId);
            }
        }

        return false;
    }

    @Autobind
    public async personCanEditRoleByTeam(person: Person, role: Role) {
        // make sure we have team roles primed
        await lastValueFrom(this.commonIntegratedArchitectureFrameworkService.getTeamRoleLocationsForRole(role.roleId));

        return role.roleLocations.some((rl) => this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureRolesEdit, rl)
            || (rl.team && this.commonTeamsAuthService.currentPersonCanEditTeam(rl.team)));
    }

    @Autobind
    public personCanEditRoles(person: Person) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureRolesEdit);
    }

    @Autobind
    public personCanEditSystems(person: Person) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureSystemEdit);
    }

    @Autobind
    public personCanConfigureSystem(person: Person, team?: Team) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureSystemConfigure, team);
    }
}
